/**
 * Converts a numeric amount to words in French, English, or Arabic.
 * Supports major currencies with correct subunit names.
 */

// ─── Currency subunit config ───────────────────────────────────────────────
const CURRENCY_NAMES = {
  EUR: { fr: ['euro', 'euros', 'centime', 'centimes'], en: ['euro', 'euros', 'cent', 'cents'], ar: ['يورو', 'يورو', 'سنت', 'سنت'] },
  USD: { fr: ['dollar', 'dollars', 'cent', 'cents'],   en: ['dollar', 'dollars', 'cent', 'cents'], ar: ['دولار', 'دولار', 'سنت', 'سنت'] },
  GBP: { fr: ['livre sterling', 'livres sterling', 'penny', 'pence'], en: ['pound', 'pounds', 'penny', 'pence'], ar: ['جنيه إسترليني', 'جنيه إسترليني', 'بنس', 'بنس'] },
  MAD: { fr: ['dirham', 'dirhams', 'centime', 'centimes'], en: ['dirham', 'dirhams', 'centime', 'centimes'], ar: ['درهم', 'درهم', 'سنتيم', 'سنتيم'] },
  TND: { fr: ['dinar', 'dinars', 'millime', 'millimes'],  en: ['dinar', 'dinars', 'millime', 'millimes'], ar: ['دينار', 'دينار', 'مليم', 'مليم'] },
  DZD: { fr: ['dinar algérien', 'dinars algériens', 'centime', 'centimes'], en: ['Algerian dinar', 'Algerian dinars', 'centime', 'centimes'], ar: ['دينار جزائري', 'دينار جزائري', 'سنتيم', 'سنتيم'] },
  XOF: { fr: ['franc CFA', 'francs CFA', 'centime', 'centimes'], en: ['CFA franc', 'CFA francs', 'centime', 'centimes'], ar: ['فرنك أفريقي', 'فرنك أفريقي', 'سنتيم', 'سنتيم'] },
  CAD: { fr: ['dollar canadien', 'dollars canadiens', 'cent', 'cents'], en: ['Canadian dollar', 'Canadian dollars', 'cent', 'cents'], ar: ['دولار كندي', 'دولار كندي', 'سنت', 'سنت'] },
  CHF: { fr: ['franc suisse', 'francs suisses', 'centime', 'centimes'], en: ['Swiss franc', 'Swiss francs', 'centime', 'centimes'], ar: ['فرنك سويسري', 'فرنك سويسري', 'سنتيم', 'سنتيم'] },
  SAR: { fr: ['riyal saoudien', 'riyals saoudiens', 'halala', 'halalas'], en: ['Saudi riyal', 'Saudi riyals', 'halala', 'halalas'], ar: ['ريال سعودي', 'ريال سعودي', 'هللة', 'هللة'] },
  AED: { fr: ['dirham des Émirats', 'dirhams des Émirats', 'fils', 'fils'], en: ['UAE dirham', 'UAE dirhams', 'fils', 'fils'], ar: ['درهم إماراتي', 'درهم إماراتي', 'فلس', 'فلس'] },
};

// ─── FRENCH ────────────────────────────────────────────────────────────────
const onesFR  = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf',
                  'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize',
                  'dix-sept', 'dix-huit', 'dix-neuf'];
const tensFR  = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante',
                  'soixante', 'quatre-vingt', 'quatre-vingt'];

function belowThousandFR(n) {
  if (n === 0) return '';
  if (n < 20) return onesFR[n];
  const t = Math.floor(n / 10), o = n % 10;
  if (t === 7) return o === 1 ? 'soixante-et-onze' : `soixante-${onesFR[10 + o]}`;
  if (t === 9) return o === 0 ? 'quatre-vingt-dix' : `quatre-vingt-${onesFR[10 + o]}`;
  if (t === 8) return o === 0 ? 'quatre-vingts' : `quatre-vingt-${onesFR[o]}`;
  const join = o === 1 && t !== 8 ? '-et-un' : (o ? `-${onesFR[o]}` : '');
  return `${tensFR[t]}${join}`;
}

function chunkFR(n, includeUn = true) {
  const h = Math.floor(n / 100), rest = n % 100;
  let result = '';
  if (h === 1) result = 'cent';
  else if (h > 1) result = `${onesFR[h]} cent${rest === 0 ? 's' : ''}`;
  if (rest) result = (result ? `${result} ` : '') + belowThousandFR(rest);
  return result;
}

function numberToWordsFR(n, currency = 'EUR') {
  if (n === 0) return `zéro ${CURRENCY_NAMES[currency]?.fr[1] || 'euro'}`;
  const main = Math.floor(n), cents = Math.round((n - main) * 100);
  const decimalPlaces = currency === 'TND' ? 3 : 2;
  const subunits = Math.round((n - main) * Math.pow(10, decimalPlaces));

  const parts = [];
  let rem = main;

  if (rem >= 1_000_000_000) {
    const b = Math.floor(rem / 1_000_000_000);
    parts.push(`${b === 1 ? 'un' : chunkFR(b)} milliard${b > 1 ? 's' : ''}`);
    rem %= 1_000_000_000;
  }
  if (rem >= 1_000_000) {
    const m = Math.floor(rem / 1_000_000);
    parts.push(`${m === 1 ? 'un' : chunkFR(m)} million${m > 1 ? 's' : ''}`);
    rem %= 1_000_000;
  }
  if (rem >= 1_000) {
    const k = Math.floor(rem / 1_000);
    parts.push(`${k === 1 ? 'mille' : `${chunkFR(k)} mille`}`);
    rem %= 1_000;
  }
  if (rem > 0) parts.push(chunkFR(rem));

  const names = CURRENCY_NAMES[currency]?.fr || ['euro', 'euros', 'centime', 'centimes'];
  let result = `${parts.join(' ')} ${main <= 1 ? names[0] : names[1]}`;
  if (subunits > 0) {
    result += ` et ${chunkFR(subunits)} ${subunits <= 1 ? names[2] : names[3]}`;
  }
  return result.charAt(0).toUpperCase() + result.slice(1);
}

// ─── ENGLISH ───────────────────────────────────────────────────────────────
const onesEN = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
                'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
                'seventeen', 'eighteen', 'nineteen'];
const tensEN = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function belowThousandEN(n) {
  if (n === 0) return '';
  if (n < 20) return onesEN[n];
  const t = Math.floor(n / 10), o = n % 10;
  if (n >= 100) {
    const h = Math.floor(n / 100), rest = n % 100;
    return `${onesEN[h]} hundred${rest ? ' ' + belowThousandEN(rest) : ''}`;
  }
  return `${tensEN[t]}${o ? '-' + onesEN[o] : ''}`;
}

function numberToWordsEN(n, currency = 'EUR') {
  if (n === 0) return `zero ${CURRENCY_NAMES[currency]?.en[1] || 'euros'}`;
  const main = Math.floor(n);
  const decimalPlaces = currency === 'TND' ? 3 : 2;
  const subunits = Math.round((n - main) * Math.pow(10, decimalPlaces));

  const parts = [];
  let rem = main;
  if (rem >= 1_000_000_000) { const b = Math.floor(rem / 1_000_000_000); parts.push(`${belowThousandEN(b)} billion`); rem %= 1_000_000_000; }
  if (rem >= 1_000_000)     { const m = Math.floor(rem / 1_000_000);     parts.push(`${belowThousandEN(m)} million`); rem %= 1_000_000; }
  if (rem >= 1_000)         { const k = Math.floor(rem / 1_000);         parts.push(`${belowThousandEN(k)} thousand`); rem %= 1_000; }
  if (rem > 0)              { parts.push(belowThousandEN(rem)); }

  const names = CURRENCY_NAMES[currency]?.en || ['euro', 'euros', 'cent', 'cents'];
  let result = `${parts.join(', ')} ${main <= 1 ? names[0] : names[1]}`;
  if (subunits > 0) result += ` and ${belowThousandEN(subunits)} ${subunits <= 1 ? names[2] : names[3]}`;
  return result.charAt(0).toUpperCase() + result.slice(1);
}

// ─── ARABIC ────────────────────────────────────────────────────────────────
const onesAR = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة',
                'عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر', 'ستة عشر',
                'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'];
const tensAR  = ['', 'عشرة', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
const hundredsAR = ['', 'مئة', 'مئتان', 'ثلاثمئة', 'أربعمئة', 'خمسمئة', 'ستمئة', 'سبعمئة', 'ثمانمئة', 'تسعمئة'];

function belowThousandAR(n) {
  if (n === 0) return '';
  const h = Math.floor(n / 100), rest = n % 100;
  const parts = [];
  if (h) parts.push(hundredsAR[h]);
  if (rest > 0 && rest < 20) parts.push(onesAR[rest]);
  else if (rest >= 20) {
    const t = Math.floor(rest / 10), o = rest % 10;
    if (o) parts.push(`${onesAR[o]} و${tensAR[t]}`);
    else parts.push(tensAR[t]);
  }
  return parts.join(' و');
}

function numberToWordsAR(n, currency = 'EUR') {
  if (n === 0) return `صفر ${CURRENCY_NAMES[currency]?.ar[1] || 'يورو'}`;
  const main = Math.floor(n);
  const decimalPlaces = currency === 'TND' ? 3 : 2;
  const subunits = Math.round((n - main) * Math.pow(10, decimalPlaces));

  const parts = [];
  let rem = main;
  if (rem >= 1_000_000_000) { const b = Math.floor(rem / 1_000_000_000); parts.push(`${belowThousandAR(b)} مليار`); rem %= 1_000_000_000; }
  if (rem >= 1_000_000)     { const m = Math.floor(rem / 1_000_000);     parts.push(`${belowThousandAR(m)} مليون`); rem %= 1_000_000; }
  if (rem >= 1_000)         { const k = Math.floor(rem / 1_000);         if (k === 1) parts.push('ألف'); else if (k === 2) parts.push('ألفان'); else parts.push(`${belowThousandAR(k)} آلاف`); rem %= 1_000; }
  if (rem > 0)              { parts.push(belowThousandAR(rem)); }

  const names = CURRENCY_NAMES[currency]?.ar || ['يورو', 'يورو', 'سنت', 'سنت'];
  let result = `${parts.join(' و')} ${names[main <= 1 ? 0 : 1]}`;
  if (subunits > 0) result += ` و${belowThousandAR(subunits)} ${names[subunits <= 1 ? 2 : 3]}`;
  return result;
}

// ─── Main export ───────────────────────────────────────────────────────────
export function numberToWords(amount, currency = 'EUR', language = 'fr') {
  const n = Math.abs(parseFloat(amount) || 0);
  // Round to avoid floating point issues
  const rounded = Math.round(n * 1000) / 1000;

  switch (language) {
    case 'en': return numberToWordsEN(rounded, currency);
    case 'ar': return numberToWordsAR(rounded, currency);
    default:   return numberToWordsFR(rounded, currency);
  }
}